import React, { useEffect, useMemo, useState } from "react";
import { gql } from "@apollo/client";
import { Button } from "@src/lib/ui/Button";
import { Popover, PopoverContent, PopoverTrigger } from "@src/lib/ui/Popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@src/lib/ui/Command";
import Text from "@src/lib/ui/Text";
import { libraryItemTypeLabel } from "./libraryItemTypeLabel";
import Paperclip1Icon from "@src/lib/icons/18px/paperclip-1";
import ChevronRightIcon from "@src/lib/icons/12px/chevron-right";
import ArrowUploadIcon from "@src/lib/icons/18px/arrow-upload";
import { AdminLibraryInput, LibraryItemType } from "@src/types.generated";
import type { OpusAgentAttachmentLibraryItemFragment } from "./useOpusAgentAttachments.generated";
import { useOpusAgentAttachMenuLibraryItemsQuery } from "./OpusAgentAttachMenu.generated";
import { OpusAgentAttachmentThumbnail } from "./OpusAgentAttachmentThumbnail";

// Duplicated from aiAssist's ChatAttachMenu / ChatAttachMenuContent (the
// outline chat is going away), widened from courses + resources to every
// Opus item type the agent reads.
const FILTERS: { type: LibraryItemType; label: string }[] = [
  { type: LibraryItemType.Course, label: "Courses" },
  { type: LibraryItemType.Module, label: "Modules" },
  { type: LibraryItemType.Skill, label: "Check-ins" },
  { type: LibraryItemType.Task, label: "Checklists" },
  { type: LibraryItemType.TrainingResource, label: "Resources" },
];
const ATTACHABLE_TYPES = FILTERS.map((filter) => filter.type);

export interface OpusAgentAttachMenuProps {
  onSelectLibraryItem: (item: OpusAgentAttachmentLibraryItemFragment) => void;
  onUploadClick: () => void;
  excludeLibraryItemIds?: string[];
  disabled?: boolean;
}

export const OpusAgentAttachMenu: React.FC<OpusAgentAttachMenuProps> = ({
  onSelectLibraryItem,
  onUploadClick,
  excludeLibraryItemIds = [],
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<LibraryItemType | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setSelectedFilter(null);
      setSearchValue("");
    }
  }, [isOpen]);

  const showMainMenu = !selectedFilter && !searchValue;
  const queryInput: AdminLibraryInput = useMemo(
    () => ({
      filters: {
        itemTypes: {
          value: selectedFilter ? [selectedFilter] : ATTACHABLE_TYPES,
        },
      },
      ...(searchValue ? { search: { value: searchValue } } : {}),
    }),
    [selectedFilter, searchValue],
  );
  const { data, previousData, loading } =
    useOpusAgentAttachMenuLibraryItemsQuery({
      variables: { input: queryInput, pagination: { limit: 30 } },
      skip: !isOpen || showMainMenu,
    });
  const items = useMemo(
    () =>
      ((data ?? previousData)?.AdminLibrary?.objects ?? []).filter(
        (item) => !excludeLibraryItemIds.includes(item.id),
      ),
    [data, previousData, excludeLibraryItemIds],
  );

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen} modal>
      <Tooltip delayDuration={300}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Attach"
              disabled={disabled}
              data-testid="opus-agent-attach"
              className="text-icon-secondary hover:text-icon-primary"
            >
              <Paperclip1Icon />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {!isOpen && <TooltipContent>Attach</TooltipContent>}
      </Tooltip>
      <PopoverContent
        align="start"
        side="top"
        motion="menu"
        className="w-80 p-0"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search library items..."
            value={searchValue}
            onValueChange={setSearchValue}
            className="border-0"
          />
          <CommandList className="max-h-80 overflow-y-auto">
            {showMainMenu ? (
              <CommandGroup>
                <CommandItem
                  onSelect={() => {
                    setIsOpen(false);
                    onUploadClick();
                  }}
                  className="cursor-pointer"
                  data-testid="opus-agent-attach-upload"
                >
                  <ArrowUploadIcon className="size-4 text-icon-secondary" />
                  <Text variant="compact-description">Upload files</Text>
                </CommandItem>
                {FILTERS.map((filter) => (
                  <CommandItem
                    key={filter.type}
                    onSelect={() => setSelectedFilter(filter.type)}
                    className="cursor-pointer"
                  >
                    <Text variant="compact-description">{filter.label}</Text>
                    <ChevronRightIcon className="ml-auto text-icon-secondary" />
                  </CommandItem>
                ))}
              </CommandGroup>
            ) : (
              <CommandGroup>
                {items.length === 0 ? (
                  <CommandEmpty>
                    {loading ? "Loading…" : "No items found."}
                  </CommandEmpty>
                ) : (
                  items.map((item) => (
                    <CommandItem
                      key={item.id}
                      value={item.id}
                      onSelect={() => {
                        onSelectLibraryItem(item);
                        setIsOpen(false);
                      }}
                      className="cursor-pointer"
                    >
                      <OpusAgentAttachmentThumbnail
                        libraryItem={item}
                        size={20}
                      />
                      <div className="min-w-0 flex-1">
                        <Text
                          variant="compact-description"
                          className="line-clamp-1"
                        >
                          {item.name?.en || "Untitled"}
                        </Text>
                      </div>
                      <Text
                        variant="helper-description"
                        className="shrink-0 text-text-tertiary"
                      >
                        {libraryItemTypeLabel(item.type)}
                      </Text>
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

gql`
  query OpusAgentAttachMenuLibraryItems(
    $input: AdminLibraryInput!
    $pagination: PaginationInput
  ) {
    AdminLibrary(input: $input, pagination: $pagination) {
      objects {
        ...OpusAgentAttachmentLibraryItem
      }
    }
  }
`;
